import { createReducer, on } from '@ngrx/store';
import { EntityState, EntityAdapter, createEntityAdapter } from '@ngrx/entity';

import { TravelerPaxResponse } from '@pax/entities/traveler-pax';
import * as paxActions from '@pax/store/actions/pax.actions';
import {
  fetchAirValidationAction,
  fetchHotelValidationAction,
  fetchPackageValidationAction,
  fetchAutoValidationAction,
  fetchAirValidationBySectorAction
} from '@booking/store/actions/booking.actions';
import { guestIdentificatorUI } from '@pax/mappers/guest-list-ui.mapper';
import { passengerIdentificatorUI } from '@pax/mappers/passenger-list-ui.mapper';
import { getDefaultDataIndex } from '@pax/mappers/default-pax.mapper';
import { addPaxLoyalty, editTravelerLoyaltyWithDelete, editTravelerLoyaltyWithUpdate } from '@pax/mappers/loyalty.mapper';
import { travelerReduxMapper, travelerMapperWithDeleteDocument } from '@pax/mappers/traveler-redux.mapper';
import { isNullOrUndefined } from '@app/util/util';

export interface TravelerPaxState extends EntityState<TravelerPaxResponse> {
  isLoadingTravelerForm: boolean;
  haveBeenLoaded: boolean;
}

function getId(traveler: TravelerPaxResponse): string {
  return traveler._id;
}

export const travelerAdapter: EntityAdapter<TravelerPaxResponse> = createEntityAdapter<TravelerPaxResponse>({
  selectId: getId
});

export const getEntityById = (id: string) => (state: TravelerPaxState) => state.entities[id];

export const initialState = travelerAdapter.getInitialState({
  ...travelerAdapter.getInitialState(),
  isLoadingTravelerForm: false,
  haveBeenLoaded: false
});


export const travelersReducer = createReducer(
  initialState,
  on(paxActions.fetchPaxTravelerListSuccessAction, (state, { response }) => (
    {
      ...travelerAdapter.setAll(travelerReduxMapper(response), state),
      haveBeenLoaded: true
    }
  )),
  on(paxActions.createPaxSuccessAction, (state, { response }) => travelerAdapter.addOne(response, state)),
  on(paxActions.updatePaxSuccessAction, (state, { response }) => travelerAdapter.updateOne(
    { id: response._id, changes: response }, state
  )),
  on(paxActions.setPassengerDefaultDataUIAction, (state, { payload, id, list }) => {
    const ui = passengerIdentificatorUI(id);
    const updates = list.map(item => ({ id: item._id, changes: { ui: null } })).concat([
      { id: payload._id, changes: { ui } }
    ]);
    return travelerAdapter.updateMany(updates, state);
  }),
  on(paxActions.setGuestDefaultDataUIAction, (state, { payload, roomId, guestId, list }) => {
    const ui = guestIdentificatorUI(roomId, guestId);
    const updates = list.map(item => ({ id: item._id, changes: { ui: null } })).concat([
      { id: payload._id, changes: { ui } }
    ]);
    return travelerAdapter.updateMany(updates, state);
  }),
  on(paxActions.configDefaultPassengerUI, (state, { config }) => {
    const { response, user, isInternationalSearch, documents, rules } = config;
    const ui = passengerIdentificatorUI(0);
    const defaultDataId = getDefaultDataIndex({ travelers: response, user, isInternationalSearch, documents, rules });
    return defaultDataId >= 0 ? travelerAdapter.updateOne({ id: response[defaultDataId]._id, changes: { ui } }, state) : state;
  }),
  on(paxActions.configDefaultGuestUI, (state, { config }) => {
    const { response, user, isInternationalSearch, documents, rules } = config;
    const ui = guestIdentificatorUI(0, 0);
    const defaultDataId = getDefaultDataIndex({ travelers: response, user, isInternationalSearch, documents, rules });
    return defaultDataId >= 0 ? travelerAdapter.updateOne({ id: response[defaultDataId]._id, changes: { ui } }, state) : state;
  }),
  on(paxActions.setTavelerPaxAction, (state, { config }) => {
    const { payload, id, guestId, list } = config;
    const ui = !isNullOrUndefined(guestId) ? guestIdentificatorUI(id, guestId) : passengerIdentificatorUI(id);
    const updates = list.map(item => ({ id: item._id, changes: { ui: null } })).concat([
      { id: payload._id, changes: { ui } }
    ]);
    return travelerAdapter.updateMany(updates, state);
  }),
  on(
    paxActions.createLoyaltyProgramSuccessAction,
    paxActions.createPackageLoyaltyProgramSuccessAction,
    (state, { response }) => {
      const id = response.traveler;
      return travelerAdapter.updateOne({
        id, changes: {
          loyalty: [...addPaxLoyalty(getEntityById(id)(state), response)]
        }
      }, state);
    }
  ),
  on(
    paxActions.updateLoyaltyProgramSuccessAction,
    paxActions.updatePackageLoyaltyProgramSuccessAction,
    (state, { response }) => {
      const id = response.traveler;
      return travelerAdapter.updateOne({
        id, changes: {
          loyalty: [...editTravelerLoyaltyWithUpdate(getEntityById(id)(state), response)]
        }
      }, state);
    }
  ),
  on(
    paxActions.deleteLoyaltyProgramSuccessAction,
    paxActions.deletePackageLoyaltyProgramSuccessAction,
    (state, { response }) => {
      const id = response.traveler;
      return travelerAdapter.updateOne({
        id, changes: {
          loyalty: [...editTravelerLoyaltyWithDelete(getEntityById(id)(state), response)]
        }
      }, state);
    }
  ),
  on(paxActions.deletePaxDocumentSuccessAction, (state, { payload }) => {
    const id = payload._id;
    const entity = getEntityById(id)(state);
    return entity ? travelerAdapter.updateOne({
      id,
      changes: travelerMapperWithDeleteDocument(getEntityById(id)(state), payload.documentId)
    }, state) : state;
  }),
  on(paxActions.setLoadingTravelerForm, (state, { isLoadingTravelerForm }) => {
    return { ...state, isLoadingTravelerForm };
  }),
  on(paxActions.deletePaxSuccessAction, (state, { payload }) => travelerAdapter.removeOne(payload._id, state)),
  on(
    fetchAirValidationAction,
    fetchAirValidationBySectorAction,
    fetchHotelValidationAction,
    fetchPackageValidationAction,
    fetchAutoValidationAction,
    () => initialState
  ),
);

export const {
  selectIds,
  selectEntities,
  selectAll,
  selectTotal,
} = travelerAdapter.getSelectors();
